/**
 * Stock Service
 * 
 * This service handles stock-related operations and data manipulation for inventory management.
 * It provides methods for tracking product inventory across different arrivages (shipments),
 * calculating stock levels, and generating statistics for business analytics.
 * 
 * The service works with the following key entities:
 * - Arrivages: Represent incoming shipments of products from suppliers (fournisseurs)
 * - Stocks: Individual stock entries that track quantity, pricing, and sales for products in each arrivage
 * - Products: Items being sold, categorized by category and brand
 */

import axiosInstance from "../../CustomAxios/axiosInstance";

// Types
export interface Stock {
    id: number;
    quantity: number;
    sold: number;
    unit_buy_price: number;
    unit_buy_price_ht: string;
    unit_sell_price: number;
    unit_sell_price_ht: string;
    created_at: string;
    updated_at: string;
    product: {
        id: number;
        name: string;
        image: string | null;
        category_name: string;
        brand_name: string;
    };
}

export interface Product {
    id: string;
    name: string;
    category_name?: string;
    brand?: {
        id: string;
        name: string;
    };
}

export interface Arrivage {
    id: number;
    reference: string | null;
    fournisseur_id: number;
    fournisseur_name: string;
    total_buy_price: string;
    total_sell_price: string;
    total_qte: number;
    products_count: number;
    created_at: string;
    stocks: Stock[];
    date:string
}

interface CreateArrivageData {
    fournisseur_id: number
    date: string
    stock_items: Array<{
        product_id: number
        quantity: number
        unit_buy_price: number
        unit_sell_price: number
    }>
}

class StockService {
    /**
     * Base API URL
     * 
     * @var string
     */
    private baseUrl: string;

    /**
     * Headers for API requests
     * 
     * @var object
     */
    private headers: Record<string, string>;

    /**
     * Constructor - Initialize the service with API configuration
     */
    constructor() {
        // Set the base URL for API requests
        this.baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
        
        // Configure default headers
        this.headers = {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        };
    }

    /**
     * Get authentication headers
     * 
     * @returns {object} Headers with authentication token
     */
    private getAuthHeaders(): Record<string, string> {
        const token = localStorage.getItem('auth_token');
        return {
            ...this.headers,
            'Authorization': `Bearer ${token}`
        };
    }

    /**
     * Get all arrivages (shipments) with their stock details
     * 
     * Each arrivage contains:
     * - Basic information (ID, reference, creation date)
     * - Supplier (fournisseur) details
     * - Financial totals (buy price, sell price)
     * - Stock entries with product information
     * 
     * @returns {Promise} Promise with arrivages data
     */
    async getAllArrivages(): Promise<Arrivage[]> {
        try {
            const response = await fetch(`${this.baseUrl}/inventory/arrivages`, {
                method: 'GET',
                headers: this.getAuthHeaders()
            });
            
            const data = await response.json();
            
            if (data.status === 'success') {
                return data.data;
            } else {
                throw new Error(data.message || 'Failed to fetch arrivages');
            }
        } catch (error) {
                        throw error;
        }
    }

    /**
     * Create a new arrivage
     * 
     * @param {object} data - Data for the new arrivage
     * @returns {Promise} Promise with created arrivage data
     */
    async createArrivage(data: CreateArrivageData): Promise<Arrivage> {
                const response = await fetch(`${this.baseUrl}/inventory/arrivages`, {
            method: 'POST',
            headers: this.getAuthHeaders(),
            body: JSON.stringify(data)
        });
        
        const dataResponse = await response.json();
        
        if (dataResponse.status === 'success') {
            return dataResponse.data;
        } else {
            throw new Error(dataResponse.message || 'Failed to create arrivage');
        }
    }

    /**
     * Delete an arrivage
     * 
     * @param {number} arrivageId - ID of the arrivage to delete
     * @returns {Promise} Promise with delete confirmation
     */
    async deleteArrivage(arrivageId: number): Promise<void> {
        try {
            const response = await fetch(`${this.baseUrl}/inventory/arrivages/${arrivageId}`, {
                method: 'DELETE',
                headers: this.getAuthHeaders()
            });
            
            const data = await response.json();
            
            if (data.status !== 'success') {
                throw new Error(data.message || 'Failed to delete arrivage');
            }
        } catch (error) {
                        throw error;
        }
    }

    /**
     * Update a stock entry
     * 
     * @param {number} stockId - ID of the stock to update
     * @param {object} stockData - Data to update
     * @returns {Promise} Promise with updated stock data
     */
    async updateStock(stockId: number, stockData: {
        quantity: number;
        unit_sell_price: number;
    }): Promise<Stock> {
        try {
            const response = await fetch(`${this.baseUrl}/inventory/stocks/${stockId}/update`, {
                method: 'POST',
                headers: this.getAuthHeaders(),
                body: JSON.stringify(stockData)
            });
            
            const data = await response.json();
            
            if (data.status === 'success') {
                return data.data;
            } else {
                throw new Error(data.message || 'Failed to update stock');
            }
        } catch (error) {
                        throw error;
        }
    }

    async getArrivageById(id: number): Promise<Arrivage> {
        try {
            const response = await fetch(`${this.baseUrl}/inventory/arrivages/${id}`, {
                method: 'GET',
                headers: this.getAuthHeaders(),
            });
            
            const data = await response.json();
            
            if (data.status === 'success') {
                return data.data;
            } else {
                throw new Error(data.message || 'Failed to fetch arrivage');
            }
        } catch (error) {
                        throw error;
        }
    }
    async fetchLowStockProducts(): Promise<any[]> {
        
         const response = await axiosInstance.get('/products/low-stock-products');
         return  response.data.data;
        
    };

    async getStocks() : Promise<Stock[]> {
        const response = await axiosInstance.get('/inventory/stocks');
        return response.data.data;
    }
}

export default new StockService();